import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { makeStyles } from "tss-react/mui";

interface Props {
	open: boolean;
	title: string;
	onClose: () => void;
	/** Right-hand action, rendered beside "Cancel". Omit both for an informational dialog with no actions. */
	confirmLabel?: string;
	onConfirm?: () => void;
	children?: React.ReactNode;
}

// Figma Modal (2874:5825, 2732:24640): 560 wide with 24px padding all round. The body stacks the
// title, a divider, and the content 16px apart; body copy is Body1 (18/24). The actions sit 32px
// below the body, right-aligned, with no divider above them (MAX-4338).
const useStyles = makeStyles()((theme) => ({
	paper: {
		width: 560,
		maxWidth: "calc(100% - 64px)",
		margin: 32,
		padding: 24,
		borderRadius: 8,
		boxShadow: "0 4px 2px rgba(0, 0, 0, 0.25)",
		display: "flex",
		flexDirection: "column",
		gap: 32,
	},
	body: {
		display: "flex",
		flexDirection: "column",
		gap: 16,
	},
	title: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 16,
		padding: 0,
	},
	titleText: {
		fontSize: 24,
		fontWeight: 700,
		lineHeight: "32px",
		color: theme.palette.custom.textPrimary,
	},
	closeButton: {
		padding: 0,
		color: theme.palette.custom.textPrimary,
	},
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 16,
		padding: 0,
		overflowY: "visible",
		color: theme.palette.custom.textPrimary,
		"& > .MuiTypography-root": {
			fontSize: 18,
			lineHeight: "24px",
		},
	},
	actions: {
		padding: 0,
		justifyContent: "flex-end",
	},
	button: {
		height: 36,
		boxSizing: "border-box",
	},
	// Figma secondary action: 2px teal outline.
	cancelButton: {
		borderWidth: 2,
		"&:hover": {
			borderWidth: 2,
		},
	},
}));

/** Shared dialog shell for the settings refresh. */
export const SettingsDialog: React.FC<Props> = ({
	open,
	title,
	onClose,
	confirmLabel,
	onConfirm,
	children,
}) => {
	const { classes, cx } = useStyles();
	return (
		<Mui.Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
			<div className={classes.body}>
				<Mui.DialogTitle component="div" className={classes.title}>
					<Mui.Typography component="h2" className={classes.titleText}>
						{title}
					</Mui.Typography>
					<Mui.IconButton className={classes.closeButton} aria-label="Close" onClick={onClose}>
						<Icons.X size={24} />
					</Mui.IconButton>
				</Mui.DialogTitle>
				<Mui.Divider />
				<Mui.DialogContent className={classes.content}>{children}</Mui.DialogContent>
			</div>
			{confirmLabel && onConfirm && (
				<Mui.DialogActions className={classes.actions}>
					<Mui.Button
						variant="outlined"
						color="primary"
						className={cx(classes.button, classes.cancelButton)}
						onClick={onClose}
					>
						Cancel
					</Mui.Button>
					<Mui.Button variant="contained" color="primary" className={classes.button} onClick={onConfirm}>
						{confirmLabel}
					</Mui.Button>
				</Mui.DialogActions>
			)}
		</Mui.Dialog>
	);
};
